import * as Sentry from '@sentry/react';
import { getBrowserId } from 'utils/getBrowserId';
import { call, select } from 'redux-saga/effects';
import { attachTranslationFingerprint } from 'features/application/sagas/translationFingerprint';
import { selectCurrentSystemRole } from 'features/application/selectors';
import { SystemRole } from 'features/room/types';
import { dropRoutineBreadcrumb } from 'utils/sentry/breadcrumbNoise';

const IDENTIFYING_KEY_DENYLIST = ['forwarded', '-ip', 'remote-', 'via', '-user'];

export function* initSentrySaga() {
  if (process.env.NODE_ENV === 'production') {
    const systemRole: SystemRole | null = yield select(selectCurrentSystemRole);

    yield call(Sentry.init, {
      dsn: process.env.REACT_APP_SENTRY_DSN,
      integrations: [Sentry.browserTracingIntegration()],
      transport: Sentry.makeBrowserOfflineTransport(Sentry.makeFetchTransport),
      normalizeDepth: 10,
      debug: false,
      environment: process.env.REACT_APP_ENV,
      release: process.env.REACT_APP_RELEASE_VERSION,
      maxBreadcrumbs: 500,
      beforeBreadcrumb: dropRoutineBreadcrumb,
      beforeSend: attachTranslationFingerprint,
      // Transient Janus signaling noise while the WS reconnects; not actionable frontend signal.
      ignoreErrors: [/is the server down\?/i],

      // v11 collects cookies, user info and request/response bodies unless told otherwise;
      // this pins the restrictive v10 defaults (sendDefaultPii was never enabled).
      dataCollection: {
        userInfo: false,
        cookies: false,
        httpHeaders: {
          request: { deny: IDENTIFYING_KEY_DENYLIST },
          response: { deny: IDENTIFYING_KEY_DENYLIST },
        },
        httpBodies: [],
        urlQueryParams: { deny: IDENTIFYING_KEY_DENYLIST },
      },

      tracesSampleRate: 0.1,
      tracePropagationTargets: [process.env.REACT_APP_API_URL as string],
      initialScope: {
        user: { id: getBrowserId() },
        // Non-system sessions carry an explicit value so an absent tag stays readable as
        // "emitted before this shipped" rather than "human".
        tags: { systemRole: systemRole ?? 'human' },
      },
    });
  }
}
